/**
 * Auto-generate navigation hotspots based on GPS positions
 * Uses distance-based pathfinding to determine connections
 */

import { convex } from "@/lib/convexClient";
import type { Hotspot, Panorama } from "@/store/editorStore";
import { api } from "../../convex/_generated/api";
import { normalizeAngle, yawPitchToPosition } from "./coordinateSystem";
import { calculateBearing, isPointBetweenPoints } from "./gpsUtils";

/**
 * Reposition navigation hotspots for a panorama when its north offset changes
 */
export async function repositionHotspotsForNorthChange(
  panorama: Panorama,
  hotspots: Hotspot[],
  allPanoramas: Panorama[],
): Promise<number> {
  // Get north offset from panorama data
  const panoramaData = panorama.data as Record<string, unknown> | undefined;
  const northOffset = typeof panoramaData?.north === "number" ? panoramaData.north : 0;

  // Get GPS location of source panorama
  const gpsLocation = panorama.gps_location as {
    lat?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  } | null;
  if (!gpsLocation) return 0;

  const latFrom = gpsLocation.lat ?? gpsLocation.latitude;
  const lngFrom = gpsLocation.lng ?? gpsLocation.longitude;

  if (latFrom === undefined || lngFrom === undefined) return 0;

  // Find all navigation hotspots for this panorama
  const navigationHotspots = hotspots.filter((h) => {
    if (h.panorama_id !== panorama.id) return false;
    const content = h.content as Record<string, unknown> | null | undefined;
    return typeof content?.linkedPanoramaId === "string";
  });

  let repositioned = 0;

  // Reposition each hotspot
  for (const hotspot of navigationHotspots) {
    const content = hotspot.content as Record<string, unknown> | null | undefined;
    const targetPanoramaId =
      typeof content?.linkedPanoramaId === "string" ? content.linkedPanoramaId : null;
    const targetPanorama = allPanoramas.find((p) => p.id === targetPanoramaId);

    if (!targetPanorama) continue;

    const targetGps = targetPanorama.gps_location as {
      lat?: number;
      lng?: number;
      latitude?: number;
      longitude?: number;
    } | null;
    if (!targetGps) continue;

    const latTo = targetGps.lat ?? targetGps.latitude;
    const lngTo = targetGps.lng ?? targetGps.longitude;

    if (latTo === undefined || lngTo === undefined) continue;

    // Calculate bearing from source to target
    const bearing = calculateBearing(latFrom, lngFrom, latTo, lngTo);

    // Convert bearing to yaw, accounting for north offset
    // GPS bearing: 0° = North, increases clockwise (90° = East, 180° = South, 270° = West)
    // Yaw: 0° = forward (-Z), positive = right (+X direction)
    // If forward (-Z) = North (bearing 0°), then right (+X) = East (bearing 90°)
    // So bearing 90° should map to yaw 90° (positive, right)
    // Formula: yaw = bearing - northOffset
    let yaw = bearing - northOffset;
    // Normalize yaw to (-180, 180] range
    yaw = normalizeAngle(yaw);

    // Convert yaw/pitch to 3D position
    const position = yawPitchToPosition(yaw, 0, 400);

    // Update hotspot position
    try {
      await convex.mutation(api.hotspots.update, { id: hotspot.id, position });
      repositioned++;
    } catch (error) {
      console.error("Failed to reposition hotspot:", error);
    }
  }

  return repositioned;
}

export interface Connection {
  fromPanoramaId: string;
  toPanoramaId: string;
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
}

export interface HotspotCreationResult {
  created: number;
  skipped: number;
  connections: Connection[];
}

/**
 * Generate navigation hotspots between panoramas based on GPS positions
 * Uses distance-based pathfinding: if panorama B is between A and C,
 * connect A↔B and B↔C but not A↔C
 */
export async function generateNavigationHotspots(
  panoramas: Panorama[],
  existingHotspots: Hotspot[],
  projectId: string,
  _userId: string,
): Promise<HotspotCreationResult> {
  // Filter panoramas with GPS coordinates
  const panoramasWithGPS = panoramas.filter((p) => {
    const gpsLocation = p.gps_location as {
      lat?: number;
      lng?: number;
      latitude?: number;
      longitude?: number;
    } | null;
    if (!gpsLocation) return false;

    const lat = gpsLocation.lat ?? gpsLocation.latitude;
    const lng = gpsLocation.lng ?? gpsLocation.longitude;

    return lat !== undefined && lng !== undefined;
  });

  if (panoramasWithGPS.length < 2) {
    return {
      created: 0,
      skipped: 0,
      connections: [],
    };
  }

  // Check which panoramas already have navigation hotspots
  const panoramasWithNavHotspots = new Set<string>();
  existingHotspots.forEach((hotspot) => {
    const content = hotspot.content as Record<string, unknown> | null | undefined;
    if (typeof content?.linkedPanoramaId === "string") {
      panoramasWithNavHotspots.add(hotspot.panorama_id);
    }
  });

  // Determine connections using distance-based pathfinding
  const connections: Connection[] = [];
  const createdHotspots: Array<Record<string, unknown>> = [];

  for (let i = 0; i < panoramasWithGPS.length; i++) {
    const panoramaA = panoramasWithGPS[i];
    const gpsA = panoramaA.gps_location as {
      lat?: number;
      lng?: number;
      latitude?: number;
      longitude?: number;
    };
    const latA = gpsA.lat ?? gpsA.latitude;
    const lngA = gpsA.lng ?? gpsA.longitude;
    if (latA === undefined || lngA === undefined) {
      continue;
    }

    // Skip if panorama already has navigation hotspots (per requirement 3c)
    if (panoramasWithNavHotspots.has(panoramaA.id)) {
      continue;
    }

    for (let j = i + 1; j < panoramasWithGPS.length; j++) {
      const panoramaB = panoramasWithGPS[j];
      const gpsB = panoramaB.gps_location as {
        lat?: number;
        lng?: number;
        latitude?: number;
        longitude?: number;
      };
      const latB = gpsB.lat ?? gpsB.latitude;
      const lngB = gpsB.lng ?? gpsB.longitude;
      if (latB === undefined || lngB === undefined) {
        continue;
      }

      // Skip if panorama B already has navigation hotspots
      if (panoramasWithNavHotspots.has(panoramaB.id)) {
        continue;
      }

      // Check if any other panorama is "between" A and B
      let hasObstacle = false;
      for (let k = 0; k < panoramasWithGPS.length; k++) {
        if (k === i || k === j) continue;

        const panoramaC = panoramasWithGPS[k];
        const gpsC = panoramaC.gps_location as {
          lat?: number;
          lng?: number;
          latitude?: number;
          longitude?: number;
        };
        const latC = gpsC.lat ?? gpsC.latitude;
        const lngC = gpsC.lng ?? gpsC.longitude;
        if (latC === undefined || lngC === undefined) {
          continue;
        }

        // Check if C is between A and B
        if (isPointBetweenPoints(latA, lngA, latB, lngB, latC, lngC)) {
          hasObstacle = true;
          break;
        }
      }

      // If no obstacle, create bidirectional connection
      if (!hasObstacle) {
        connections.push({
          fromPanoramaId: panoramaA.id,
          toPanoramaId: panoramaB.id,
          fromLat: latA,
          fromLng: lngA,
          toLat: latB,
          toLng: lngB,
        });
        connections.push({
          fromPanoramaId: panoramaB.id,
          toPanoramaId: panoramaA.id,
          fromLat: latB,
          fromLng: lngB,
          toLat: latA,
          toLng: lngA,
        });
      }
    }
  }

  // Create hotspots for each connection
  for (const connection of connections) {
    const fromPanorama = panoramasWithGPS.find((p) => p.id === connection.fromPanoramaId);
    const toPanorama = panoramasWithGPS.find((p) => p.id === connection.toPanoramaId);

    if (!fromPanorama || !toPanorama) continue;

    // Calculate bearing from fromPanorama to toPanorama
    const bearing = calculateBearing(
      connection.fromLat,
      connection.fromLng,
      connection.toLat,
      connection.toLng,
    );

    // Get north offset from panorama data (default to 0 if not set)
    const panoramaData = fromPanorama.data as Record<string, unknown> | undefined;
    const northOffset = typeof panoramaData?.north === "number" ? panoramaData.north : 0;

    // Convert bearing to yaw, accounting for north offset
    // GPS bearing: 0° = North, increases clockwise (90° = East, 180° = South, 270° = West)
    // Yaw: 0° = forward (-Z), positive = right (+X direction)
    // If forward (-Z) = North (bearing 0°), then right (+X) = East (bearing 90°)
    // So bearing 90° should map to yaw 90° (positive, right)
    // Formula: yaw = bearing - northOffset
    let yaw = bearing - northOffset;
    // Normalize yaw to (-180, 180] range
    yaw = normalizeAngle(yaw);

    // Convert yaw/pitch to 3D position (pitch = 0 for horizontal)
    const position = yawPitchToPosition(yaw, 0, 400);

    // Create SVG icon matching existing navigation hotspot style
    const hotspotSVG = `<svg width='256' height='256' viewBox='0 0 256 256'>
      <circle cx='128' cy='128' r='110' fill='white' opacity='0.5'/>
      <circle cx='128' cy='128' r='90' fill='white' opacity='1'/>
      <circle cx='128' cy='128' r='70' fill='#2196f3'/>
      <g transform='translate(128, 128) scale(3)'>
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'>
          <path d='M13 12V20H11V12H4L12 4L20 12H13Z'></path>
        </svg>
      </g>
    </svg>`;

    // Count existing hotspots for order
    const existingCount = existingHotspots.filter((h) => h.panorama_id === fromPanorama.id).length;

    const newHotspot = {
      project_id: projectId,
      panorama_id: fromPanorama.id,
      name: `${toPanorama.name} (nav)`,
      type: "svg",
      position: position,
      scale: { x: 1, y: 1, z: 1 },
      content: {
        icon: "→",
        svg: hotspotSVG,
        title: `${toPanorama.name} (nav)`,
        description: `Click to navigate to ${toPanorama.name}`,
        color: "#2196f3",
        linkedPanoramaId: toPanorama.id,
      },
      visible: true,
      order: existingCount,
    };

    createdHotspots.push(newHotspot);
  }

  // Insert all hotspots into database
  let created = 0;
  const _skipped = 0;

  if (createdHotspots.length > 0) {
    await Promise.all(
      createdHotspots.map((hotspot) =>
        convex.mutation(api.hotspots.create, {
          project_id: hotspot.project_id as string,
          panorama_id: hotspot.panorama_id as string,
          name: hotspot.name as string,
          type: hotspot.type as string,
          content: hotspot.content,
          position: hotspot.position,
          rotation: hotspot.rotation,
          scale: hotspot.scale,
          visible: hotspot.visible,
          order: hotspot.order as number,
        }),
      ),
    );
    created = createdHotspots.length;
  }

  return {
    created,
    skipped: panoramasWithNavHotspots.size,
    connections,
  };
}
